// Giriş yapan yöneticinin grup endpoint izinlerini yükler ve sayfa/menü erişimini
// denetlemek için yol-bazlı (path) kontrol sağlar.
//
// Güvenlik tasarımı — "fail open":
//   • Grubu olmayan (üst yönetici) kullanıcı → tam erişim.
//   • İzinler yüklenemezse / boşsa / ayrıştırılamazsa → tam erişim.
//   • Yalnızca olumlu ayrıştırılmış, dolu bir izin kümesi varsa kısıtlama uygulanır.
// Böylece backend yanıt biçimi beklenenden farklı olsa bile kimse yanlışlıkla kilitlenmez.

import MainMenuConfig from '@/core/config/MainMenuConfig'

const state = () => ({
    loaded: false,        // izinler başarıyla yüklendi ve uygulanıyor
    fullAccess: true,     // kısıtlama yok (varsayılan: güvenli taraf)
    paths: [],            // grubun erişebildiği API yolları (normalize, ör. "module/slider/group")
    forbidden: false,     // aktif sayfada 403 alındı → "erişiminiz yok" ekranı göster
})

const getters = {
    permissionReady(state) {
        return state.loaded || state.fullAccess
    },
    // base: menü öğesinin gerektirdiği API kök yolu (ör. "link/manager")
    canPath: (state) => (base) => {
        if (state.fullAccess) return true
        if (!state.loaded) return true            // henüz yüklenmedi → engelleme
        if (!base) return true                    // izin tanımlı değil → serbest
        const b = String(base).replace(/^\/+/, '').toLowerCase()
        return state.paths.some(p => p === b || p.startsWith(b + '/'))
    },
}

// İzin kataloğundaki (admin/group/endpoint) tüm uç düğümleri (endpoint) düzleştir
function collectLeaves(nodes) {
    const out = []
    for (const n of nodes || []) {
        const isLeaf = n && n.id != null && !Array.isArray(n.children) && !Array.isArray(n.endpoints)
        if (isLeaf) out.push(n)
        if (Array.isArray(n.children)) out.push(...collectLeaves(n.children))
        if (Array.isArray(n.endpoints)) out.push(...collectLeaves(n.endpoints))
    }
    return out
}

// Menü yapılandırmasında tanımlı tüm izin kök yolları (güvenlik ağı için)
function collectMenuBases(items, acc) {
    for (const it of items || []) {
        if (it.permission) acc.add(String(it.permission).replace(/^\/+/, '').toLowerCase())
        if (Array.isArray(it.sub)) collectMenuBases(it.sub, acc)
        if (Array.isArray(it.pages)) collectMenuBases(it.pages, acc)
    }
    return acc
}

function normalizePath(p) {
    let s = String(p || '')
        .replace(/^https?:\/\/[^/]+/i, '')   // tam URL gelirse alan adını at
        .replace(/[?#].*$/, '')              // query/fragment'i at
        .replace(/^\/+/, '')                 // baştaki / 'leri at
        .toLowerCase()
        .trim()
    // Bilinen API ön eklerini at (FE göreli yolları öneksizdir: ör. "module/slider/group")
    s = s
        .replace(/^[a-z0-9_-]+\/api\/v\d+\//, '')  // admin/api/v1/...
        .replace(/^api\/v\d+\//, '')               // api/v1/...
        .replace(/^api\//, '')                     // api/...
    return s
}

// Uçuştaki tek yükleme isteği + "bu sayfa yüklemesinde çözüldü" bayrağı.
// created + mounted (veya art arda dispatch'ler) aynı isteği paylaşsın, sunucuya
// tekrar tekrar gitmesin. Modül düzeyi → sayfa yenilenince sıfırlanır (yenilemede 1 kez).
// NOT: state.fullAccess başlangıçta true (güvenli varsayılan) olduğundan koruma
// olarak KULLANILAMAZ; ilk yüklemeyi de atlardı.
let _loadInFlight = null
let _loadDone = false

const actions = {
    async load({commit, rootGetters}) {
        // Bu sayfa yüklemesinde zaten çözüldü → yeniden istek atma (yenileme başına 1 kez).
        if (_loadDone) return
        // Devam eden bir yükleme varsa onu paylaş (çift istek engellenir).
        if (_loadInFlight) return _loadInFlight
        _loadInFlight = (async () => {
        const user = rootGetters.currentUser
        // Oturum biçimine toleranslı grup id çözümü
        const groupId = user?.group?.id
            ?? user?.group_id
            ?? (typeof user?.group === 'number' || typeof user?.group === 'string' ? user.group : null)
        // Grubu olmayan kullanıcı (üst yönetici) → tam erişim
        if (!groupId) {
            commit('setFullAccess', true)
            return
        }
        try {
            // _skipForbidden: bu isteklerdeki 403 global "erişiminiz yok" ekranını TETİKLEMEZ;
            // izin kataloğuna erişimi olmayan kısıtlı kullanıcıda sessizce fail-open olur.
            const vm = this._vm
            const cfg = {_skipForbidden: true}
            const [catRes, grpRes] = await Promise.all([
                vm.axios.get(vm.endpoint.get('admin_group_endpoint'), cfg),
                vm.axios.get(vm.endpoint.get('admin_group') + '/' + groupId, cfg),
            ])
            const catalog = catRes?.data?.data || []
            const group = grpRes?.data?.data || {}
            // id → path haritası (alan adı backend'e göre değişebilir)
            const idToPath = {}
            for (const ep of collectLeaves(catalog || [])) {
                const path = normalizePath(ep.route || ep.path || ep.url || ep.uri || ep.endpoint)
                if (path) idToPath[ep.id] = path
            }
            // grubun erişebildiği yollar
            const paths = []
            for (const e of (group?.endpoints || [])) {
                const path = idToPath[e.id]
                if (path) paths.push(path)
            }
            // Olumlu, dolu küme yoksa güvenli tarafta kal (fail open)
            if (!paths.length) {
                commit('setFullAccess', true)
                return
            }
            // Güvenlik ağı: ayrıştırılan yollar bilinen menü kök yollarının hiçbiriyle
            // eşleşmiyorsa (muhtemel biçim uyuşmazlığı) kısıtlama uygulama, kilitlenmeyi önle.
            const bases = [...collectMenuBases(MainMenuConfig, new Set())]
            const anyMatch = bases.some(b => paths.some(p => p === b || p.startsWith(b + '/')))
            // Teşhis: menü gizleme çalışmıyorsa bu çıktıyı paylaşın
            // eslint-disable-next-line no-console
            console.info('[permission]', {
                groupId,
                catalogLeaves: Object.keys(idToPath).length,
                groupEndpoints: (group?.endpoints || []).length,
                permittedPaths: paths.length,
                samplePermitted: paths.slice(0, 8),
                enforcing: anyMatch,
            })
            if (!anyMatch) {
                commit('setFullAccess', true)
                return
            }
            commit('setPaths', paths)
        } catch (e) {
            // Yüklenemezse kimseyi kilitleme
            commit('setFullAccess', true)
        }
        })()
        try { await _loadInFlight } finally { _loadInFlight = null }
    },

    reset({commit}) {
        commit('reset')
    },
}

const mutations = {
    setPaths(state, paths) {
        state.paths = paths
        state.loaded = true
        state.fullAccess = false
    },
    setFullAccess(state, val) {
        state.fullAccess = !!val
        state.loaded = false
        state.paths = []
    },
    setForbidden(state, val) {
        state.forbidden = !!val
    },
    reset(state) {
        state.loaded = false
        state.fullAccess = true
        state.paths = []
        state.forbidden = false
    },
}

export default {namespaced: true, state, getters, actions, mutations}
